import './documentAssistantComponent.scss';
import React, {useEffect, useRef, useState} from 'react';
import {SvgIcon} from '../../icons/svgIcon';
import {Icons} from '../../icons/icons';
import {useTranslation} from 'react-i18next';
import {getCurrentLanguage} from '../../i18n-service';
import {
    fetchStreamedAiResponse,
    getHibuAssistantRequestWithDocuments,
    getLastConversationId,
    uuidv4,
} from '../common/utils';
import {ChatWindow} from '../common/chatWindow';
import {AiAssistantConversationContext, ChatType, DEFAULT_ASSISTANT_CONVERSATION_CONTEXT, Role,} from '../common/types';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {useStateConversation, useStateConversationsOfChatType,} from '../duck/assistant-hooks';
import {
    doAddMessageRequest,
    doCreateConversationRequest,
    doDeleteConversationRequest,
    doLoadConversationsRequest,
    doUpdateConversationContextRequest,
} from '../duck/assistant-actions';
import {MessageBox} from '../../message-box/message-box';
import {showError} from '../../message-box/message-box-actions';
import {ChatInput} from '../common/chatInput';

export const GeneralAssistantComponent = React.memo(() => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const currentLanguage = getCurrentLanguage();

    const conversationsOfChatType = useStateConversationsOfChatType(ChatType.GENERAL);
    const [currentConversationId, setCurrentConversationId] = useState<string>(() => {
        return getLastConversationId(conversationsOfChatType, ChatType.GENERAL);
    });
    const conversation = useStateConversation(currentConversationId);
    const conversationContext: AiAssistantConversationContext =
        conversation?.context ?? DEFAULT_ASSISTANT_CONVERSATION_CONTEXT;
    const isLoadingConversations = useSelector((state: DefaultRootState) => state.assistant.isLoadingConversations);

    const [answer, setAnswer] = useState('');

    useEffect(() => {
        dispatch(doLoadConversationsRequest(ChatType.GENERAL));
        messagesEndRef?.current?.scrollIntoView({behavior: 'smooth'});
    }, [dispatch]);

    useEffect(() => {
        if (!conversation && !isLoadingConversations) {
            dispatch(
                doCreateConversationRequest(ChatType.GENERAL, currentConversationId, t('assistant.welcome'), Role.ASSISTANT),
            );
        }
    }, [conversation, currentConversationId, dispatch, t, isLoadingConversations]);

    const fetchResponseAndUpdateConversation = async (
        userMessage: string,
        contextOverride: AiAssistantConversationContext = conversationContext,
    ) => {
        if (!userMessage?.trim() || !conversation) {
            return;
        }

        dispatch(doUpdateConversationContextRequest(currentConversationId, contextOverride));
        dispatch(doAddMessageRequest(currentConversationId, {
            text: userMessage,
            role: Role.USER,
            metadata: {context: contextOverride},
        }));

        const request = getHibuAssistantRequestWithDocuments(
            [],
            currentLanguage,
            userMessage,
            conversation,
            contextOverride,
        );
        fetchStreamedAiResponse(request, {
            onToken: (token) => setAnswer((prev) => prev + token),
        })
            .then((hibuAssistantMessage) => {
                dispatch(doAddMessageRequest(currentConversationId, hibuAssistantMessage));
                setAnswer('');
            })
            .catch((error) => {
                console.error('Error in fetchStreamedAiResponse:', error);
                setAnswer('');
                dispatch(showError(ChatType.GENERAL, 'assistant.error.responseError'));
            });
    };

    const handleNewConversation = () => {
        const newId = uuidv4();
        dispatch(doCreateConversationRequest(ChatType.GENERAL, newId, t('assistant.welcome'), Role.ASSISTANT));
    };

    const handleDelete = (id: string) => {
        dispatch(doDeleteConversationRequest(id));
    };

    if (!conversationsOfChatType) {
        return null;
    }

    return (
        <div id="hibu-assistant-content">
            <MessageBox id={ChatType.GENERAL}/>
            <div className="hibu-assistant">
                <div className="conversation-history">
                    <div className={'conversation-wrapper-create'} key={-1} onClick={() => handleNewConversation()}>
                        <div className={'icon-wrapper'}>
                            <SvgIcon icon={Icons.ADD} id="icon-settings" className={['add', 'icon'].join(' ')}/>
                        </div>
                        <div className={'text-wrapper'}>{t('assistant.new-chat')}</div>
                    </div>
                    {conversationsOfChatType.map((conversation) =>
                        conversation.conversationMessages ? (
                            <div
                                className={[
                                    'conversation-wrapper',
                                    conversation.id === currentConversationId ? 'active' : '',
                                ].join(' ')}
                                key={conversation.id}
                            >
                                <div
                                    className={'text-wrapper'}
                                    onClick={() => setCurrentConversationId(conversation.id)}
                                >
                                    {conversation.conversationMessages.find((m) => m.role === 'user')?.text ||
                                        t('assistant.new-chat-title')}
                                </div>
                                <div className={'icon-wrapper'} onClick={() => handleDelete(conversation.id)}>
                                    <SvgIcon
                                        icon={Icons.DELETE}
                                        id="icon-settings"
                                        className={['delete', 'icon'].join(' ')}
                                    />
                                </div>
                            </div>
                        ) : null,
                    )}
                </div>
                <div className="wrapper">
                    <ChatWindow
                        conversation={conversation}
                        currentMessage={{
                            text: answer,
                            role: Role.ASSISTANT,
                        }}
                    />
                    <div className="chat-sugestions"></div>
                    <ChatInput
                        conversationId={currentConversationId}
                        welcomeMessage={t('assistant.input-placeholder')}
                        getAiResponse={fetchResponseAndUpdateConversation}
                        handleResetConversation={conversation ? () => handleDelete(conversation.id) : () => null}
                    />
                </div>
            </div>
        </div>
    );
});
